import React from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { notePreview } from '../utils/note-utils';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CardMetaText } from './CardMetaText';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { EllipsisVertical, Paperclip } from 'lucide-react';
import { cn } from '../lib/utils';
import { useIsMobile } from '../hooks/use-mobile';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import { SlidingFolderInputIcon } from './SlidingFolderInputIcon';

const NOTE_PREVIEW_MAX_LEN = 200;

export interface NoteCardNote {
  id: string;
  content: string;
  attachmentFileName: string | null;
  updatedAt: string;
}

interface NoteCardProps {
  note: NoteCardNote;
  onEditClick: () => void;
  onDownloadAttachment?: () => void;
  selected?: boolean;
  onMoveToProject?: () => void;
  onMoveToTrash?: () => void;
}

export const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onEditClick,
  onDownloadAttachment,
  selected = false,
  onMoveToProject,
  onMoveToTrash,
}) => {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const hasActions = Boolean(onMoveToProject || onMoveToTrash);
  const showInlineActions = hasActions && !isMobile;

  const attachmentClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (onDownloadAttachment) onDownloadAttachment();
  };

  return (
    <Card
      className={cn(
        'group/note relative border-0 shadow-none rounded-[10px] cursor-pointer transition-all hover:bg-card',
        selected && 'bg-muted/40',
      )}
      style={{ border: '1px solid var(--border)' }}
      onClick={onEditClick}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="min-w-0 flex-1">
          <p className="m-0 whitespace-pre-wrap break-words text-sm leading-relaxed">
            {notePreview(note.content, NOTE_PREVIEW_MAX_LEN) || t('no_content') || '(No content)'}
          </p>
          {note.attachmentFileName && (
            <CardMetaText className="mt-2 flex items-center gap-2">
              <span
                role={onDownloadAttachment ? 'button' : undefined}
                tabIndex={onDownloadAttachment ? 0 : undefined}
                onClick={onDownloadAttachment ? attachmentClick : undefined}
                onKeyDown={
                  onDownloadAttachment
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          onDownloadAttachment();
                        }
                      }
                    : undefined
                }
                className={cn(
                  'inline-flex items-center gap-1',
                  onDownloadAttachment && 'cursor-pointer underline underline-offset-2',
                )}
              >
                <Paperclip className="h-3 w-3" />
                {note.attachmentFileName}
              </span>
            </CardMetaText>
          )}
        </div>
        {showInlineActions ? (
          <TooltipProvider>
            <div
              className="flex shrink-0 items-start gap-1 opacity-80 transition-opacity group-hover/note:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              {onMoveToProject ? (
                <ButtonTooltip tooltip={t('move_note_to_project_tooltip')} side="bottom">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="group h-8 w-8"
                    aria-label={t('move_to_project')}
                    onClick={onMoveToProject}
                  >
                    <SlidingFolderInputIcon />
                  </Button>
                </ButtonTooltip>
              ) : null}
              {onMoveToTrash ? (
                <ButtonTooltip tooltip={t('move_note_to_trash_tooltip')} side="bottom">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="group h-8 w-8"
                    aria-label={t('move_to_trash')}
                    onClick={onMoveToTrash}
                  >
                    <RotatingTrashIcon />
                  </Button>
                </ButtonTooltip>
              ) : null}
            </div>
          </TooltipProvider>
        ) : null}
        {hasActions && isMobile ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t('note_actions')}
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {onMoveToProject ? (
                <DropdownMenuItem
                  className="group"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToProject();
                  }}
                >
                  <SlidingFolderInputIcon className="mr-2" />
                  {t('move_to_project')}
                </DropdownMenuItem>
              ) : null}
              {onMoveToTrash ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToTrash();
                  }}
                  className="group"
                >
                  <RotatingTrashIcon className="mr-2" />
                  {t('move_to_trash')}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </Card>
  );
};
